/**
 * 角色配置表单组件
 *
 * 可复用表单组件，用于配置规划者和监督者的：
 * - 显示名称 (≤20 chars)
 * - 提示词 (≤5000 chars)
 * - 可用工具 (supervisor不能有WRITE_TOOLS)
 * - 温度 (0.0-2.0)
 * - 模型选择器
 * - 实时验证
 */

import React, { useCallback, useMemo } from 'react';
import { useAgentTeamConfigStore } from '../../store/agentTeamConfigStore';
import {
  TOOL_CATEGORIES,
  WRITE_TOOLS,
  type RoleConfig,
} from '../../types/agentTeam';

interface RoleConfigFormProps {
  role: 'planner' | 'supervisor';
  config: RoleConfig | null;
  disabled?: boolean;
}

const AVAILABLE_MODELS = [
  { id: 'gpt-4o', name: 'GPT-4o' },
  { id: 'gpt-4o-mini', name: 'GPT-4o Mini' },
  { id: 'gpt-4-turbo', name: 'GPT-4 Turbo' },
  { id: 'gpt-3.5-turbo', name: 'GPT-3.5 Turbo' },
  { id: 'claude-3-5-sonnet-20241022', name: 'Claude 3.5 Sonnet' },
  { id: 'claude-3-5-haiku-20241022', name: 'Claude 3.5 Haiku' },
  { id: 'deepseek-chat', name: 'DeepSeek Chat' },
  { id: 'deepseek-reasoner', name: 'DeepSeek Reasoner' },
];

export const RoleConfigForm: React.FC<RoleConfigFormProps> = ({
  role,
  disabled = false,
}) => {
  const {
    updateConfig,
    getFormState,
    getFormError,
    validateForm,
    formErrors,
  } = useAgentTeamConfigStore();

  const formState = getFormState(role);

  const handleDisplayNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateConfig(role, { display_name: e.target.value });
    },
    [role, updateConfig]
  );

  const handlePromptChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      updateConfig(role, { prompt: e.target.value });
    },
    [role, updateConfig]
  );

  const handleTemperatureChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateConfig(role, { temperature: parseFloat(e.target.value) });
    },
    [role, updateConfig]
  );

  const handleModelChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newModel = e.target.value;
      updateConfig(role, {
        llm_config: formState.llm_config
          ? { ...formState.llm_config, model: newModel }
          : { model: newModel },
      });
    },
    [role, updateConfig, formState.llm_config]
  );

  const handleToolToggle = useCallback(
    (tool: string) => {
      const currentTools = formState.enabled_tools;
      const newTools = currentTools.includes(tool)
        ? currentTools.filter((t) => t !== tool)
        : [...currentTools, tool];
      updateConfig(role, { enabled_tools: newTools });
    },
    [role, updateConfig, formState.enabled_tools]
  );

  const handleCategoryToggle = useCallback(
    (_categoryKey: string, tools: readonly string[]) => {
      const currentTools = formState.enabled_tools;
      const categoryTools = tools;
      const allSelected = categoryTools.every((t) => currentTools.includes(t));

      let newTools: string[];
      if (allSelected) {
        newTools = currentTools.filter((t) => !categoryTools.includes(t));
      } else {
        newTools = [...new Set([...currentTools, ...categoryTools])];
      }
      updateConfig(role, { enabled_tools: newTools });
    },
    [role, updateConfig, formState.enabled_tools]
  );

  const displayNameError = getFormError('display_name');
  const promptError = getFormError('prompt');
  const temperatureError = getFormError('temperature');

  const isSupervisor = role === 'supervisor';

  const availableCategories = useMemo(() => {
    if (isSupervisor) {
      return {
        read: TOOL_CATEGORIES.read,
        web: TOOL_CATEGORIES.web,
        other: TOOL_CATEGORIES.other,
      };
    }
    return TOOL_CATEGORIES;
  }, [isSupervisor]);

  const isFormValid = useMemo(() => {
    validateForm(role);
    return formErrors.size === 0;
  }, [role, validateForm, formErrors]);

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          显示名称 <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={formState.display_name}
          onChange={handleDisplayNameChange}
          className={`
            w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent
            ${displayNameError ? 'border-red-300' : 'border-gray-300'}
          `}
          placeholder={`例如: ${role === 'planner' ? '总裁' : '监事'}`}
          maxLength={20}
          disabled={disabled}
        />
        <div className="flex justify-between items-center mt-1">
          {displayNameError ? (
            <p className="text-sm text-red-600">{displayNameError}</p>
          ) : (
            <p className="text-xs text-gray-600">最多20个字符</p>
          )}
          <p className="text-xs text-gray-600">{formState.display_name.length}/20</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          提示词 <span className="text-red-500">*</span>
        </label>
        <textarea
          value={formState.prompt}
          onChange={handlePromptChange}
          className={`
            w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent min-h-[120px]
            ${promptError ? 'border-red-300' : 'border-gray-300'}
          `}
          placeholder={`描述${role === 'planner' ? '规划者' : '监督者'}的角色和行为方式...`}
          maxLength={5000}
          disabled={disabled}
        />
        <div className="flex justify-between items-center mt-1">
          {promptError ? (
            <p className="text-sm text-red-600">{promptError}</p>
          ) : (
            <p className="text-xs text-gray-600">最多5000个字符</p>
          )}
          <p className="text-xs text-gray-600">{formState.prompt.length}/5000</p>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-2">
          可用工具
          {isSupervisor && (
            <span className="text-xs text-gray-600 ml-2">
              (监督者不能使用写入工具)
            </span>
          )}
        </label>
<div className="space-y-3">
          {Object.entries(availableCategories).map(([key, category]) => {
            const tools = category.tools;
            const allSelected = tools.every((t) => formState.enabled_tools.includes(t));
            const someSelected = tools.some((t) => formState.enabled_tools.includes(t));

            return (
              <div key={key}>
                <div className="flex items-center gap-2 mb-1">
                  <label
                    className={`
                      inline-flex items-center cursor-pointer
                      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={() => handleCategoryToggle(key, tools)}
                      className="sr-only"
                      disabled={disabled}
                    />
                    <span
                      className={`
                        px-2 py-0.5 rounded text-sm font-medium
                        ${allSelected
                          ? 'bg-paper-100 text-paper-800 border border-paper-300'
                          : someSelected
                            ? 'bg-gray-200 text-gray-800 border border-gray-300'
                            : 'bg-gray-100 text-gray-600 border border-gray-200'
                        }
                      `}
                    >
                      {allSelected ? '✓' : someSelected ? '◐' : '○'} {category.name}
                    </span>
                  </label>
                </div>
                <p className="text-xs text-gray-600 mb-2">{category.description}</p>
                <div className="flex flex-wrap gap-2">
                  {tools.map((tool) => {
                    const isSelected = formState.enabled_tools.includes(tool);
                    const isWriteTool = WRITE_TOOLS.has(tool);
                    const isDisabledForSupervisor = isSupervisor && isWriteTool;

                    return (
                      <label
                        key={tool}
                        className={`
                          inline-flex items-center px-3 py-1.5 rounded-full text-sm cursor-pointer
                          transition-colors duration-150
                          ${isSelected && !isDisabledForSupervisor
                            ? 'bg-paper-100 text-paper-800 border border-paper-300'
                            : 'bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200'
                          }
                          ${isDisabledForSupervisor ? 'opacity-40 cursor-not-allowed' : ''}
                          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                        `}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToolToggle(tool)}
                          className="sr-only"
                          disabled={disabled || isDisabledForSupervisor}
                        />
                        {tool}
                        {isDisabledForSupervisor && (
                          <span className="ml-1 text-xs text-red-600">⚠</span>
                        )}
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          温度: {formState.temperature.toFixed(1)}
        </label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.1"
          value={formState.temperature}
          onChange={handleTemperatureChange}
          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          disabled={disabled}
        />
        <div className="flex justify-between text-xs text-gray-800 mt-1">
          <span>精确 (0.0)</span>
          <span>平衡 (1.0)</span>
          <span>创意 (2.0)</span>
        </div>
        {temperatureError && (
          <p className="mt-1 text-sm text-red-600">{temperatureError}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">
          模型选择
        </label>
        <select
          value={formState.llm_config?.model || ''}
          onChange={handleModelChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-paper-500 focus:border-transparent"
          disabled={disabled}
        >
          <option value="">默认模型</option>
          {AVAILABLE_MODELS.map((model) => (
            <option key={model.id} value={model.id}>
              {model.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-600">
          选择特定的LLM模型，或使用系统默认模型
        </p>
      </div>

      <div className="pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2">
          <div
            className={`
              w-3 h-3 rounded-full
              ${isFormValid ? 'bg-green-500' : 'bg-red-500'}
            `}
          />
          <span className="text-sm text-gray-700">
            {isFormValid ? '表单验证通过' : '表单存在错误'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default RoleConfigForm;